<script setup lang="ts">
import emblaCarouselVue from 'embla-carousel-vue';

import type { Media } from '~/packages/commerce/types/product';

withDefaults(
    defineProps<{
        images: Media[];
        hasImagePreview?: boolean;
        height: string;
        isLoading?: boolean;
        loading: 'lazy' | 'eager';
    }>(),
    { loading: 'lazy' },
);

const [emblaRef, emblaApi] = emblaCarouselVue({ axis: 'y' });

const currentSlideIndex = ref(0);
const canScrollPrevious = ref(false);
const canScrollNext = ref(true);

const thumbnails = ref(null);
const thumbnailsScrollTop = ref(0);
const updateScrollTop = () => {
    thumbnailsScrollTop.value = thumbnails.value?.scrollTop;
};

onMounted(() => {
    emblaApi.value?.on('select', () => {
        if (emblaApi.value) {
            currentSlideIndex.value = emblaApi.value.selectedScrollSnap();
            canScrollPrevious.value = emblaApi.value.canScrollPrev();
            canScrollNext.value = emblaApi.value.canScrollNext();
        }
    });
});
</script>

<template>
    <section class="embla flex w-full gap-5">
        <div class="relative" :style="`height: ${height}`">
            <Transition
                enter-active-class="transition-opacity duration-300 ease-[cubic-bezier(.75,.11,.85,.01)]"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-300 ease-[cubic-bezier(.25,.89,.15,.99)]"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <button
                    v-if="thumbnailsScrollTop > 0"
                    class="absolute left-0 top-0 flex min-h-[32px] w-full items-center justify-center rounded-lg bg-gray-900 py-2 text-white"
                    aria-label="Nach oben scrollen"
                    @click="
                        thumbnails?.scrollTo({ top: 0, behavior: 'smooth' })
                    "
                >
                    <JamBaseIcon icon-name="arrow-up" />
                </button>
            </Transition>
            <div
                ref="thumbnails"
                class="flex w-[100px] flex-col gap-3 overflow-hidden"
                :style="`height: ${height};`"
                @scroll="updateScrollTop"
            >
                <template v-if="isLoading">
                    <JamBaseSkeletonLoader
                        v-for="it in 3"
                        :key="it"
                        height="!h-[75px] !w-[120px] mb-3 rounded"
                    />
                </template>
                <template v-else>
                    <button
                        v-for="(image, index) in images"
                        :key="image.url"
                        class="rounded border border-gray-100"
                        :class="[
                            index === currentSlideIndex &&
                                'border !border-gray-900',
                        ]"
                        @click="emblaApi?.scrollTo(index)"
                    >
                        <NuxtImg
                            :placeholder="[50, 25, 20, 8]"
                            :loading="loading"
                            format="webp"
                            width="200"
                            height="113"
                            :src="image.url"
                            :modifiers="{ pos: 'top' }"
                            class="rounded"
                            :alt="`Vorschaubild: ${image.alt}`"
                            decoding="auto"
                        />
                    </button>
                </template>
            </div>
            <Transition
                enter-active-class="transition-opacity duration-300 ease-[cubic-bezier(.75,.11,.85,.01)]"
                enter-from-class="opacity-0"
                enter-to-class="opacity-100"
                leave-active-class="transition-opacity duration-300 ease-[cubic-bezier(.25,.89,.15,.99)]"
                leave-from-class="opacity-100"
                leave-to-class="opacity-0"
            >
                <button
                    v-if="
                        thumbnailsScrollTop + thumbnails?.clientHeight <
                        thumbnails?.scrollHeight
                    "
                    class="absolute bottom-0 flex min-h-[32px] w-full items-center justify-center rounded-lg bg-gray-900 text-white"
                    aria-label="Nach unten scrollen"
                    @click="
                        thumbnails?.scrollTo({
                            top: thumbnails?.scrollHeight,
                            behavior: 'smooth',
                        })
                    "
                >
                    <JamBaseIcon icon-name="arrow-down" />
                </button>
            </Transition>
        </div>
        <div>
            <div ref="emblaRef" class="embla__viewport !overflow-hidden">
                <div
                    :class="`embla__container !aspect-video flex flex-col touch-pan-x touch-pinch-zoo`"
                    :style="`height: ${height};`"
                >
                    <template v-if="isLoading">
                        <JamBaseSkeletonLoader
                            additional-classes="flex-[0_0_100%]"
                            height="rounded w-full h-full"
                        />
                    </template>
                    <template v-else>
                        <div
                            v-for="image in images"
                            :key="image.url"
                            class="embla__slide flex-[0_0_100%] overflow-y-auto border border-gray-200"
                        >
                            <CommBaseModal
                                v-if="hasImagePreview"
                                additional-classes="p-7"
                            >
                                <template #trigger>
                                    <NuxtImg
                                        :loading="loading"
                                        format="webp"
                                        :alt="image.alt"
                                        :src="image.url"
                                        decoding="auto"
                                        class="cursor-zoom-in"
                                    />
                                </template>
                                <NuxtImg
                                    loading="lazy"
                                    format="webp"
                                    fit="inside"
                                    quality="100"
                                    :alt="image.alt"
                                    :src="image.url"
                                    decoding="auto"
                                />
                            </CommBaseModal>
                        </div>
                    </template>
                </div>
            </div>
            <JamBaseText
                v-if="images"
                class="mt-3 flex w-full justify-center text-gray-600"
            >
                {{ images[currentSlideIndex]?.description ?? '&nbsp;' }}
            </JamBaseText>
        </div>
    </section>
</template>
